import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Check, Star } from "lucide-react";
import { Button } from "@/components/ui/button";

const plan = {
  name: "Básico",
  description: "Para freelancers y profesionistas",
  price: { monthly: 350, semiannual: 315, annual: 280 },
  features: [
    "Declaraciones mensuales",
    "Declaración anual incluida",
    "Facturación CFDI 4.0 ilimitada",
    "Asesoría WhatsApp",
    "1 régimen fiscal",
    "Asistente IA básico",
    "Diagnóstico fiscal",
  ],
};

type BillingPeriod = "monthly" | "semiannual" | "annual";

export function PricingSection() {
  const [billing, setBilling] = useState<BillingPeriod>("monthly");

  const getDiscount = () => {
    if (billing === "semiannual") return "-10%";
    if (billing === "annual") return "-20%";
    return null;
  };

  return (
    <section className="section-padding" id="precios">
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
            Planes simples. <span className="gradient-text">Sin sorpresas.</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
            Elige el plan que mejor se adapte a tus necesidades. Todos incluyen lo esencial.
          </p>

          {/* Billing Toggle */}
          <div className="inline-flex items-center p-1 rounded-xl bg-muted">
            {[
              { key: "monthly", label: "Mensual" },
              { key: "semiannual", label: "Semestral" },
              { key: "annual", label: "Anual" },
            ].map((option) => (
              <button
                key={option.key}
                onClick={() => setBilling(option.key as BillingPeriod)}
                className={`relative px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                  billing === option.key
                    ? "bg-card text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {option.label}
                {option.key !== "monthly" && billing === option.key && (
                  <span className="absolute -top-2 -right-2 px-1.5 py-0.5 text-xs bg-accent text-accent-foreground rounded-full">
                    {getDiscount()}
                  </span>
                )}
              </button>
            ))}
          </div>
        </motion.div>

        <div className="max-w-md mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="h-full rounded-3xl p-8 transition-all bg-primary text-primary-foreground shadow-2xl border-2 border-secondary">
              <div className="mb-6">
                <h3 className="text-2xl font-heading font-bold mb-2">{plan.name}</h3>
                <p className="text-primary-foreground/70">{plan.description}</p>
              </div>

              <div className="mb-6">
                <div className="flex items-baseline gap-1">
                  <span className="text-4xl font-heading font-bold">${plan.price[billing].toLocaleString()}</span>
                  <span className="text-primary-foreground/70">/mes</span>
                </div>
                {billing !== "monthly" && (
                  <p className="text-sm mt-1 text-secondary">
                    Pagas ${(plan.price[billing] * (billing === "annual" ? 12 : 6)).toLocaleString()} MXN
                  </p>
                )}
              </div>

              <ul className="space-y-3 mb-8">
                {plan.features.map((feature, j) => (
                  <li key={j} className="flex items-start gap-3">
                    <Check className="w-5 h-5 flex-shrink-0 text-secondary" />
                    <span className="text-sm text-primary-foreground/90">{feature}</span>
                  </li>
                ))}
              </ul>

              <Button size="lg" className="w-full bg-secondary text-secondary-foreground hover:bg-secondary/90" asChild>
                <Link to="/registro">Contratar ahora</Link>
              </Button>
            </div>
          </motion.div>
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center text-sm text-muted-foreground mt-8"
        >
          Todos los precios son en MXN. IVA incluido. Cancela cuando quieras.
        </motion.p>
      </div>
    </section>
  );
}
